<script setup lang="ts">
/** [#e03fdbc2] Макет iPhone для лендинга. Только картинка: никаких команд и запросов. */
import {
  Calendar, Camera, Clock, Cloud, Compass, FileText, Heart, Image, Lock, Mail, Map, MessageCircle, Music, Phone, Send, Settings, Video, Wallet, Wifi,
} from 'lucide-vue-next';
import type { LandingCopy, LockState } from './copy';
import { useNow } from './useNow';

defineProps<{ state: LockState; text: LandingCopy['phone'] }>();

const { time } = useNow();

// Настоящие иконки из App Store: приложения, которые партнёр может оставить для оплаты.
const payApps = [
  { id: 'payme', name: 'Payme' }, { id: 'click', name: 'Click' }, { id: 'uzum', name: 'Uzum Bank' }, { id: 'paynet', name: 'Paynet' },
  { id: 'revolut', name: 'Revolut' }, { id: 'paypal', name: 'PayPal' }, { id: 'cashapp', name: 'Cash App' }, { id: 'ozon', name: 'Ozon Банк' },
];

const apps = [
  { icon: Mail, c: 'linear-gradient(180deg,#3fb0ff,#0a6cff)' }, { icon: Calendar, c: 'linear-gradient(180deg,#ff6a5f,#e8261c)' },
  { icon: Image, c: 'linear-gradient(180deg,#ffc24b,#ff8a00)' }, { icon: Camera, c: 'linear-gradient(180deg,#8e8e93,#48484a)' },
  { icon: Map, c: 'linear-gradient(180deg,#6ee07f,#22b14c)' }, { icon: Music, c: 'linear-gradient(180deg,#ff6b8b,#f5174e)' },
  { icon: Wallet, c: 'linear-gradient(180deg,#3a3a3c,#0c0c0e)' }, { icon: Cloud, c: 'linear-gradient(180deg,#8fe0ff,#2fa8ee)' },
  { icon: Video, c: 'linear-gradient(180deg,#5de08a,#16a34a)' }, { icon: Clock, c: 'linear-gradient(180deg,#48484a,#111113)' },
  { icon: FileText, c: 'linear-gradient(180deg,#ffe066,#f5b800)' }, { icon: Heart, c: 'linear-gradient(180deg,#ff8fb0,#ff2d6f)' },
];
const dock = [
  { icon: Phone, c: 'linear-gradient(180deg,#6ee07f,#22b14c)' }, { icon: MessageCircle, c: 'linear-gradient(180deg,#7ceb6f,#2cc23a)' },
  { icon: Compass, c: 'linear-gradient(180deg,#3fb0ff,#0a6cff)' }, { icon: Settings, c: 'linear-gradient(180deg,#aeaeb2,#6c6c70)' },
];
const softDock = [dock[0], dock[1], dock[3]];
</script>

<template>
  <div class="pm" aria-hidden="true">
    <span class="pm-btn pm-btn--l1" /><span class="pm-btn pm-btn--l2" /><span class="pm-btn pm-btn--r" />
    <div class="pm-bezel">
      <div class="pm-screen">
        <div class="pm-island"><i /></div>
        <div class="pm-status">
          <span>{{ time }}</span>
          <span class="pm-icons"><span class="pm-bars"><i /><i /><i /><i /></span><Wifi /><span class="pm-batt"><i /></span></span>
        </div>
        <Transition name="pm-fade">
          <div v-if="state === 'paid'" key="paid" class="pm-view pm-view--paid">
            <div class="pm-push">
              <span class="pm-push-icon"><Send /></span>
              <span class="pm-push-text"><b>{{ text.paidPush }}</b><small>{{ text.paidPushText }}</small></span>
            </div>
            <div class="pm-grid">
              <span v-for="(a, i) in apps" :key="i" class="pm-app" :style="{ background: a.c }"><component :is="a.icon" /></span>
            </div>
            <div class="pm-dock">
              <span v-for="(a, i) in dock" :key="i" class="pm-app" :style="{ background: a.c }"><component :is="a.icon" /></span>
            </div>
          </div>
          <div v-else-if="state === 'soft'" key="soft" class="pm-view pm-view--soft">
            <div class="pm-note">
              <b>{{ text.softTitle }}</b>
              <span>{{ text.softText }}</span>
            </div>
            <div class="pm-grid pm-grid--pay">
              <span v-for="a in payApps" :key="a.id">
                <img :src="`/app-icons/${a.id}.webp`" alt="" width="56" height="56" loading="lazy" decoding="async" />
                <small>{{ a.name }}</small>
              </span>
            </div>
            <div class="pm-kept">
              <span v-for="(a, i) in softDock" :key="i">
                <span class="pm-app" :style="{ background: a.c }"><component :is="a.icon" /></span>
                <small>{{ text.apps[i] }}</small>
              </span>
            </div>
          </div>
          <div v-else key="locked" class="pm-view pm-view--locked">
            <span class="pm-lock"><Lock /></span>
            <b class="pm-lock-title">{{ text.lockedTitle }}</b>
            <span class="pm-lock-text">{{ text.lockedText }}</span>
            <span class="pm-call"><Phone />{{ text.call }}</span>
            <span class="pm-foot">{{ text.store }} · {{ text.serial }} F2LXK7Q9HG</span>
          </div>
        </Transition>
        <div class="pm-home" />
      </div>
    </div>
  </div>
</template>
